/**
 * Copyright (c) 2021 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	robin herrmann - itemis AG
 *
 */
import { dia, shapes, ui, util } from "@clientio/rappid";
import { MARKER } from "./base/validation";
import { BaseNode, BaseView } from "./base/base-node";
import { ElementController } from "./base/element-controller";
import { PreferenceStore } from "../services/preference-store";

const Position = ui.Halo.HandlePosition;

export class Synchronization extends BaseNode {
  defaults() {
    return {
      ...super.defaults,
      type: "Synchronization",
      size: { width: 8, height: 30 },
      fillOpacity: 1,
      embedable: false,
      linkable: true,

      attrs: {
        root: {
          magnetSelector: "body",
        },
        marker: {
          text: MARKER,
          fontFamily: "FontAwesome",
          fontSize: 13,
          x: -5,
          y: 0,
          opacity: 0,
        },
        body: {
          refHeight: "100%",
          refWidth: "100%",
          rx: 3,
          strokeWidth: 0,
        },
      },
    };
  }

  markup = [
    {
      tagName: "rect",
      selector: "body",
    },
    {
      tagName: "text",
      selector: "marker",
    },
  ];

  getFillColor(): string {
    return this.attr(["body", "fill"]);
  }

  setFillColor(color: string | null) {
    this.attr(["body", "fill"], color);
    if (color === null) {
      this.removeAttr(["body", "fill"]);
    }
  }

  getBorderColor(): string {
    return this.attr(["body", "stroke"]);
  }

  setBorderColor(color: string | null) {
    this.attr(["body", "stroke"], color);
    if (color === null) {
      this.removeAttr(["body", "stroke"]);
      this.attr(["body", "strokeWidth"], 0);
    } else if (this.getBorderWidth() === 0) {
      this.attr(["body", "strokeWidth"], 1);
    }
  }

  getBorderWidth(): number {
    return this.attr(["body", "strokeWidth"]) || 0;
  }

  setBorderWidth(width: number) {
    this.attr(["body", "strokeWidth"], width);
  }

  resetDefaults() {
    this.setFillColor(null);
    this.setBorderColor(null);
  }
}

export class SynchronizationView extends BaseView {
  getMinSize(direction: dia.Direction): { width: number; height: number } {
    return { width: 8, height: 30 };
  }
}

export class SynchronizationController extends ElementController {
  getHaloConfig() {
    const config = super.getHaloConfig();
    config.push({
      name: "rotate",
      position: Position.SW,
      events: {
        pointerdown: "startRotating",
        pointermove: "doRotate",
        pointerup: "stopBatch",
      },
      attrs: {
        ".handle": {
          "data-tooltip-class-name": "small",
          "data-tooltip": "Rotate the object",
          "data-tooltip-position": "right",
          "data-tooltip-padding": 15,
        },
      },
    });
    return config;
  }
}

util.assign(shapes, {
  Synchronization,
  SynchronizationView,
  SynchronizationController,
});
